import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO, isToday, isSameDay, isSameMonth, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, addMonths } from "date-fns";
import { es } from "date-fns/locale";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { FolderSelect } from "@/components/FolderSelect";
import { useCoffee } from "@/context/CoffeeContext";
import { PAGE, BTN_PRIMARY, BTN_GHOST, INPUT, CARD, ICON_BTN } from "@/lib/styles";
import { Plus, CalendarDays, List, LayoutGrid, Check, Trash2, Pencil, ChevronLeft, ChevronRight, X, Flag, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PRIORITY = {
  high: { label: "Alta", dot: "#f43f5e", cls: "text-rose-500 bg-rose-500/10 border-rose-500/20" },
  medium: { label: "Media", dot: "#f59e0b", cls: "text-amber-500 bg-amber-500/10 border-amber-500/20" },
  low: { label: "Baja", dot: "#0ea5e9", cls: "text-sky-500 bg-sky-500/10 border-sky-500/20" },
};
const STATUS = { todo: "Por hacer", doing: "En progreso", done: "Hecho" };
const COLS = ["todo", "doing", "done"];

const dueDate = (t) => (t.due_date ? parseISO(t.due_date.length === 10 ? t.due_date + "T00:00:00" : t.due_date) : null);
const isOverdue = (t) => t.status !== "done" && t.due_date && new Date(t.due_date.length === 10 ? t.due_date + "T23:59:59" : t.due_date) < new Date();

function PriorityTag({ p }) {
  const pr = PRIORITY[p] || PRIORITY.medium;
  return <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${pr.cls}`}><Flag size={9} /> {pr.label}</span>;
}

const emptyTask = { title: "", description: "", status: "todo", priority: "medium", due_date: "", reminder_minutes: 0, folder_id: "", link_type: "", link_id: "" };

function TaskModal({ open, initial, notes, onClose, onSaved }) {
  const [t, setT] = useState(emptyTask);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setT({ ...emptyTask, ...initial }); }, [open, initial]);
  if (!open) return null;
  const set = (k) => (e) => setT((s) => ({ ...s, [k]: e.target.value }));
  const datePart = t.due_date ? t.due_date.slice(0, 10) : "";
  const timePart = t.due_date && t.due_date.length > 10 ? t.due_date.slice(11, 16) : "";
  const setDue = (date, time) => {
    if (!date) { setT((s) => ({ ...s, due_date: "" })); return; }
    setT((s) => ({ ...s, due_date: time ? `${date}T${time}` : date }));
  };

  const save = async () => {
    if (!t.title.trim()) { toast.info("Escribe un título para la tarea"); return; }
    setSaving(true);
    const payload = {
      title: t.title.trim(), description: t.description, status: t.status, priority: t.priority,
      due_date: t.due_date || null, folder_id: t.folder_id || null,
      reminder_minutes: Number(t.reminder_minutes) || 0,
      link_type: t.link_id ? "note" : null, link_id: t.link_id || null,
    };
    try {
      if (t.id) await api.put(`/tasks/${t.id}`, payload);
      else await api.post("/tasks", payload);
      onSaved();
      onClose();
      toast.success(t.id ? "Tarea actualizada" : "Tarea creada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="task-modal">
      <div className="w-full max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-medium text-lg">{t.id ? "Editar tarea" : "Nueva tarea"}</h3>
          <button onClick={onClose} className={ICON_BTN} data-testid="task-modal-close"><X size={16} /></button>
        </div>
        <input className={`${INPUT} mb-3`} placeholder="¿Qué hay que hacer?" value={t.title} onChange={set("title")} data-testid="task-title" autoFocus />
        <textarea className={`${INPUT} mb-3 h-24 resize-none`} placeholder="Detalles (opcional)" value={t.description} onChange={set("description")} data-testid="task-description" />
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="font-mono2 text-[11px] text-slate-400">Fecha</label>
            <input type="date" className={INPUT} value={datePart} onChange={(e) => setDue(e.target.value, timePart)} data-testid="task-due" />
          </div>
          <div>
            <label className="font-mono2 text-[11px] text-slate-400">Hora (cita)</label>
            <input type="time" className={INPUT} value={timePart} onChange={(e) => setDue(datePart || format(new Date(), "yyyy-MM-dd"), e.target.value)} data-testid="task-time" />
          </div>
          <div>
            <label className="font-mono2 text-[11px] text-slate-400">Prioridad</label>
            <select className={INPUT} value={t.priority} onChange={set("priority")} data-testid="task-priority">
              <option value="high">Alta</option><option value="medium">Media</option><option value="low">Baja</option>
            </select>
          </div>
          <div>
            <label className="font-mono2 text-[11px] text-slate-400">Estado</label>
            <select className={INPUT} value={t.status} onChange={set("status")} data-testid="task-status">
              <option value="todo">Por hacer</option><option value="doing">En progreso</option><option value="done">Hecho</option>
            </select>
          </div>
          <div className="flex flex-col col-span-2">
            <label className="font-mono2 text-[11px] text-slate-400 mb-1">Carpeta</label>
            <FolderSelect value={t.folder_id || ""} onChange={(v) => setT((s) => ({ ...s, folder_id: v }))} />
          </div>
        </div>
        {timePart && (
          <div className="-mt-1 mb-3">
            <label className="font-mono2 text-[11px] text-slate-400">Avísame antes</label>
            <select className={INPUT} value={t.reminder_minutes || 0} onChange={(e) => setT((s) => ({ ...s, reminder_minutes: Number(e.target.value) }))} data-testid="task-reminder">
              <option value={0}>A la hora exacta</option>
              <option value={10}>10 minutos antes</option>
              <option value={30}>30 minutos antes</option>
              <option value={60}>1 hora antes</option>
              <option value={120}>2 horas antes</option>
              <option value={1440}>1 día antes</option>
            </select>
            <p className="font-body text-[11px] text-amber-600 dark:text-amber-400 mt-1.5 flex items-center gap-1.5">
              <Clock size={12} /> Recibirás una alarma {(t.reminder_minutes || 0) > 0 ? "anticipada" : "a esta hora"} (activa las notificaciones del navegador).
            </p>
          </div>
        )}
        <div className="mb-4">
          <label className="font-mono2 text-[11px] text-slate-400">Vincular a nota/mapa (opcional)</label>
          <select className={INPUT} value={t.link_id || ""} onChange={set("link_id")} data-testid="task-link">
            <option value="">Ninguno</option>
            {notes.map((n) => <option key={n.id} value={n.id}>{n.type === "mindmap" ? "🗺️ " : "📝 "}{n.title}</option>)}
          </select>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className={BTN_GHOST} data-testid="task-cancel">Cancelar</button>
          <button onClick={save} className={BTN_PRIMARY} disabled={saving} data-testid="task-save">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

function TaskRow({ t, onToggle, onEdit, onDelete }) {
  const overdue = isOverdue(t);
  return (
    <div className={`${CARD} p-3 flex items-center gap-3 group`} data-testid={`task-row-${t.id}`}>
      <button onClick={() => onToggle(t)} className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${t.status === "done" ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300 dark:border-slate-600"}`} data-testid={`task-toggle-${t.id}`}>
        {t.status === "done" && <Check size={12} />}
      </button>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onEdit(t)}>
        <p className={`font-body text-sm truncate ${t.status === "done" ? "line-through text-slate-400" : ""}`}>{t.title}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <PriorityTag p={t.priority} />
          {t.due_date && <span className={`font-mono2 text-[10px] ${overdue ? "text-rose-500 font-semibold" : "text-slate-400"}`}>{format(dueDate(t), t.due_date.length > 10 ? "d 'de' MMMM yyyy · HH:mm" : "d 'de' MMMM yyyy", { locale: es })}{overdue ? " · vencida" : ""}</span>}
        </div>
      </div>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100">
        <button onClick={() => onEdit(t)} className={ICON_BTN} data-testid={`task-edit-${t.id}`}><Pencil size={13} /></button>
        <button onClick={() => onDelete(t)} className={ICON_BTN} data-testid={`task-delete-${t.id}`}><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

export default function Agenda() {
  const navigate = useNavigate();
  const { refresh } = useCoffee();
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [view, setView] = useState("list");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(emptyTask);
  const [month, setMonth] = useState(new Date());
  const [dragId, setDragId] = useState(null);
  const [dayModal, setDayModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks");
      setTasks(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); api.get("/notes").then(({ data }) => setNotes(data)).catch(() => {}); }, [load]);

  const openNew = (prefill = {}) => { setEditing({ ...emptyTask, ...prefill }); setModal(true); };
  const openEdit = (t) => { setEditing(t); setModal(true); };

  const toggle = async (t) => {
    const status = t.status === "done" ? "todo" : "done";
    setTasks((arr) => arr.map((x) => (x.id === t.id ? { ...x, status } : x)));
    try { await api.put(`/tasks/${t.id}`, { status }); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const move = async (id, status) => {
    setTasks((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
    try { await api.put(`/tasks/${id}`, { status }); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const remove = async (t) => {
    setTasks((arr) => arr.filter((x) => x.id !== t.id));
    try { await api.delete(`/tasks/${t.id}`); toast.success("Tarea eliminada"); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const onSaved = () => { load(); refresh(); };

  // ----- List grouping -----
  const active = tasks.filter((t) => t.status !== "done");
  const done = tasks.filter((t) => t.status === "done");
  const overdue = active.filter((t) => isOverdue(t)).sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
  const today = active.filter((t) => t.due_date && isToday(dueDate(t)) && !isOverdue(t));
  const upcoming = active.filter((t) => t.due_date && !isToday(dueDate(t)) && !isOverdue(t)).sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
  const noDate = active.filter((t) => !t.due_date);

  const Section = ({ title, items, color }) => items.length ? (
    <div className="mb-6">
      <h3 className={`font-heading text-sm font-medium mb-2 ${color}`}>{title} <span className="text-slate-400 font-normal">({items.length})</span></h3>
      <div className="flex flex-col gap-2">{items.map((t) => <TaskRow key={t.id} t={t} onToggle={toggle} onEdit={openEdit} onDelete={remove} />)}</div>
    </div>
  ) : null;

  // ----- Calendar -----
  const days = eachDayOfInterval({ start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }) });
  const tasksOn = (d) => tasks.filter((t) => t.due_date && isSameDay(dueDate(t), d));

  const ViewBtn = ({ id, Icon, label }) => (
    <button onClick={() => setView(id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${view === id ? "bg-indigo-600 text-white" : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"}`} data-testid={`view-${id}`}>
      <Icon size={16} /> <span className="hidden sm:inline">{label}</span>
    </button>
  );

  return (
    <div className={`min-h-screen ${PAGE} font-body`}>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading font-light text-3xl sm:text-4xl tracking-tight">Agenda</h1>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400">Tareas, calendario y tablero en un solo lugar.</p>
          </div>
          <button onClick={() => openNew()} className={BTN_PRIMARY} data-testid="new-task-button"><Plus size={16} /> Nueva tarea</button>
        </div>

        <div className="flex items-center gap-1 mb-6 bg-slate-100 dark:bg-slate-900 rounded-xl p-1 w-fit">
          <ViewBtn id="list" Icon={List} label="Lista" />
          <ViewBtn id="calendar" Icon={CalendarDays} label="Calendario" />
          <ViewBtn id="kanban" Icon={LayoutGrid} label="Tablero" />
        </div>

        {loading ? (
          <div className="py-20 flex justify-center text-slate-400"><Loader2 className="animate-spin" size={26} /></div>
        ) : tasks.length === 0 ? (
          <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-12 text-center">
            <CalendarDays size={30} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
            <h3 className="font-heading font-normal text-lg">Tu agenda está vacía</h3>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-4">Crea tu primera tarea con fecha y prioridad.</p>
            <button onClick={() => openNew()} className={`${BTN_PRIMARY} mx-auto`}><Plus size={16} /> Nueva tarea</button>
          </div>
        ) : view === "list" ? (
          <div data-testid="agenda-list">
            <Section title="Vencidas" items={overdue} color="text-rose-500" />
            <Section title="Hoy" items={today} color="text-indigo-500" />
            <Section title="Próximas" items={upcoming} color="text-amber-500" />
            <Section title="Sin fecha" items={noDate} color="text-slate-500" />
            <Section title="Completadas" items={done} color="text-emerald-500" />
          </div>
        ) : view === "calendar" ? (
          <div data-testid="agenda-calendar">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-medium text-lg capitalize">{format(month, "MMMM yyyy", { locale: es })}</h2>
              <div className="flex gap-1">
                <button onClick={() => setMonth((m) => addMonths(m, -1))} className={ICON_BTN} data-testid="cal-prev"><ChevronLeft size={18} /></button>
                <button onClick={() => setMonth(new Date())} className={BTN_GHOST}>Hoy</button>
                <button onClick={() => setMonth((m) => addMonths(m, 1))} className={ICON_BTN} data-testid="cal-next"><ChevronRight size={18} /></button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-px bg-slate-200 dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800">
              {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => <div key={d} className="bg-slate-50 dark:bg-slate-900 text-center py-2 font-mono2 text-[11px] text-slate-400">{d}</div>)}
              {days.map((d) => {
                const dayTasks = tasksOn(d);
                return (
                  <div key={d.toISOString()} onClick={() => setDayModal(d)} className={`min-h-[92px] p-1.5 cursor-pointer bg-white dark:bg-slate-950 hover:bg-indigo-50 dark:hover:bg-slate-900 ${isSameMonth(d, month) ? "" : "opacity-40"}`} data-testid={`cal-day-${format(d, "yyyy-MM-dd")}`}>
                    <div className={`text-xs font-mono2 mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday(d) ? "bg-indigo-600 text-white" : "text-slate-500"}`}>{format(d, "d")}</div>
                    <div className="flex flex-col gap-1">
                      {dayTasks.slice(0, 3).map((t) => (
                        <div key={t.id} onClick={(e) => { e.stopPropagation(); openEdit(t); }} className={`text-[10px] px-1.5 py-0.5 rounded truncate border ${PRIORITY[t.priority].cls} ${t.status === "done" ? "line-through opacity-60" : ""}`} title={t.title}>{t.title}</div>
                      ))}
                      {dayTasks.length > 3 && <span className="text-[10px] text-slate-400">+{dayTasks.length - 3} más</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="agenda-kanban">
            {COLS.map((col) => {
              const items = tasks.filter((t) => t.status === col);
              return (
                <div key={col} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragId) { move(dragId, col); setDragId(null); } }} className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-3 min-h-[200px]" data-testid={`kanban-col-${col}`}>
                  <h3 className="font-heading font-medium text-sm mb-3 flex items-center justify-between">
                    {STATUS[col]} <span className="text-slate-400 font-normal">{items.length}</span>
                  </h3>
                  <div className="flex flex-col gap-2">
                    {items.map((t) => (
                      <div key={t.id} draggable onDragStart={() => setDragId(t.id)} className={`${CARD} p-3 cursor-grab active:cursor-grabbing group`} data-testid={`kanban-card-${t.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <p className={`font-body text-sm ${t.status === "done" ? "line-through text-slate-400" : ""}`} onClick={() => openEdit(t)}>{t.title}</p>
                          <button onClick={() => remove(t)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500 shrink-0" data-testid={`kanban-delete-${t.id}`}><Trash2 size={13} /></button>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <PriorityTag p={t.priority} />
                          {t.due_date && <span className={`font-mono2 text-[10px] flex items-center gap-1 ${isOverdue(t) ? "text-rose-500" : "text-slate-400"}`}><Clock size={9} />{format(dueDate(t), "dd MMM", { locale: es })}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <TaskModal open={modal} initial={editing} notes={notes} onClose={() => setModal(false)} onSaved={onSaved} />

      {dayModal && (
        <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDayModal(null)} data-testid="day-modal">
          <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-medium text-lg capitalize">{format(dayModal, "EEEE d 'de' MMMM yyyy", { locale: es })}</h3>
              <button onClick={() => setDayModal(null)} className={ICON_BTN} data-testid="day-modal-close"><X size={16} /></button>
            </div>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-4">¿Qué tienes pendiente o qué deseas hacer en esta fecha?</p>
            {(() => {
              const items = tasksOn(dayModal);
              return items.length ? (
                <div className="flex flex-col gap-2 mb-4">
                  {items.map((t) => <TaskRow key={t.id} t={t} onToggle={toggle} onEdit={(x) => { setDayModal(null); openEdit(x); }} onDelete={remove} />)}
                </div>
              ) : (
                <p className="font-body text-sm text-slate-400 mb-4">No tienes nada agendado para este día.</p>
              );
            })()}
            <button onClick={() => { const d = format(dayModal, "yyyy-MM-dd"); setDayModal(null); openNew({ due_date: d }); }} className={`${BTN_PRIMARY} w-full`} data-testid="day-new-task">
              <Plus size={16} /> Nueva tarea en esta fecha
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
